/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { createEntity } from 'jotai/entity';
import { SystemWorkspaceId, systemWorkspaceIdSlug } from '../SystemWorkspaceId';
import { WorkspaceObject } from '../WorkspaceObject';
import { OriginType, RunType, SanctioningBodyEnum } from 'models/viewModels';

export const defaultPractice: WorkspaceObject = {
  id: SystemWorkspaceId.DefaultPractice,
  name: 'Default Practice',
  slug: systemWorkspaceIdSlug.defaultPractice,
  owner: 'system',
  sessionTypes: [RunType.PRACTICE],
  sanctioningBody: SanctioningBodyEnum.NASCAR,
  activeView: 't0',
  views: {
    byId: {
      t0: {
        id: 't0',
        name: 'Combined View',
        visualizations: ['v0', 'v4', 'v5', 'v6'],
        layouts: 'vl0',
      },
      t1: {
        id: 't1',
        name: 'Weather',
        visualizations: ['v2', 'v3', 'v7'],
        layouts: 'vl1',
      },
      t2: {
        id: 't2',
        name: 'Track Temp',
        visualizations: ['v9'],
        layouts: 'vl4',
      },
      t3: {
        id: 't3',
        name: 'Laptime Rank',
        visualizations: ['v14'],
        layouts: 'vl5',
      },
      t4: {
        id: 't4',
        name: 'Practice View',
        visualizations: ['v8', 'v10', 'v11', 'v12', 'v13'],
        layouts: 'vl6',
      },
      t5: {
        id: 't5',
        name: 'Fast Laps',
        visualizations: ['v15'],
        layouts: 'vl7',
      },
    },
    allIds: ['t0', 't4', 't1', 't2', 't3', 't5'],
  },
  viewLayouts: {
    byId: {
      vl0: [
        {
          w: 3,
          h: 33,
          x: 0,
          y: 0,
          i: 'v0',
          moved: false,
          static: false,
        },
        {
          w: 9,
          h: 22,
          x: 3,
          y: 0,
          i: 'v4',
          moved: false,
          static: false,
        },
        {
          w: 3,
          h: 17,
          x: 0,
          y: 33,
          i: 'v5',
          moved: false,
          static: false,
        },
        {
          w: 9,
          h: 28,
          x: 3,
          y: 22,
          i: 'v6',
          moved: false,
          static: false,
        },
      ],
      vl1: [
        {
          w: 6,
          h: 26,
          x: 0,
          y: 0,
          i: 'v2',
          moved: false,
          static: false,
        },
        {
          w: 6,
          h: 50,
          x: 6,
          y: 0,
          i: 'v3',
          moved: false,
          static: false,
        },
        {
          w: 6,
          h: 24,
          x: 0,
          y: 26,
          i: 'v7',
          moved: false,
          static: false,
        },
      ],
      vl4: [
        {
          w: 12,
          h: 51,
          x: 0,
          y: 0,
          i: 'v9',
          moved: false,
          static: false,
        },
      ],
      vl5: [
        {
          w: 12,
          h: 50,
          x: 0,
          y: 0,
          i: 'v14',
          moved: false,
          static: false,
        },
      ],
      vl6: [
        {
          w: 3,
          h: 34,
          x: 0,
          y: 0,
          i: 'v8',
          moved: false,
          static: false,
        },
        {
          w: 9,
          h: 26,
          x: 3,
          y: 25,
          i: 'v10',
          moved: false,
          static: false,
        },
        {
          w: 4.333333333333333,
          h: 25,
          x: 3,
          y: 0,
          i: 'v11',
          moved: false,
          static: false,
        },
        {
          w: 3,
          h: 17,
          x: 0,
          y: 34,
          i: 'v12',
          moved: false,
          static: false,
        },
        {
          w: 4.666666666666667,
          h: 25,
          x: 7.333333333333333,
          y: 0,
          i: 'v13',
          moved: false,
          static: false,
        },
      ],
      vl7: [
        {
          w: 12,
          h: 50,
          x: 0,
          y: 0,
          i: 'v15',
          moved: false,
          static: false,
        },
      ],
    },
    allIds: ['vl0', 'vl1', 'vl4', 'vl5', 'vl6', 'vl7'],
  },
  visualizations: {
    byId: {
      v0: {
        id: 'v0',
        name: 'Race Order Table',
        type: 'raceOrderTable',
        settings: 'vs0',
      },
      v2: {
        id: 'v2',
        name: 'Weather',
        type: 'weather',
        settings: 'vs2',
      },
      v3: {
        id: 'v3',
        name: 'Weather Radar Map',
        type: 'weatherMap',
        settings: 'vs3',
      },
      v9: {
        id: 'v9',
        name: 'Track Temperature',
        type: 'trackTemperature',
        settings: 'vs9',
      },
      v4: {
        id: 'v4',
        name: 'Lap Times Table',
        type: 'lapTimesTable',
        settings: 'vs4',
      },
      v5: {
        id: 'v5',
        name: 'Global Competitor Groups',
        type: 'competitorGroupsDock',
        settings: 'vs5',
      },
      v6: {
        id: 'v6',
        name: 'Lap Times Graph',
        type: 'lapTimesGraph',
        settings: 'vs6',
      },
      v7: {
        id: 'v7',
        name: 'Forecast',
        type: 'weather',
        settings: 'vs7',
      },
      v8: {
        id: 'v8',
        name: 'Race Order Table',
        type: 'raceOrderTable',
        settings: 'vs8',
      },
      v10: {
        id: 'v10',
        name: 'Lap Times Graph',
        type: 'lapTimesGraph',
        settings: 'vs10',
      },
      v11: {
        id: 'v11',
        name: 'Lap Times Table',
        type: 'lapTimesTable',
        settings: 'vs11',
      },
      v12: {
        id: 'v12',
        name: 'Global Competitor Groups',
        type: 'competitorGroupsDock',
        settings: 'vs12',
      },
      v13: {
        id: 'v13',
        name: 'Track View Map',
        type: 'trackViewMap',
        settings: 'vs13',
      },
      v14: {
        id: 'v14',
        name: 'Rank Analysis Report',
        type: 'rankAnalysisReport',
        settings: 'vs14',
      },
      v15: {
        id: 'v15',
        name: 'Fastest 10-20-30',
        type: 'fastestLapsTable',
        settings: 'vs15',
      },
    },
    allIds: ['v0', 'v2', 'v3', 'v9', 'v4', 'v5', 'v6', 'v7', 'v8', 'v10', 'v11', 'v12', 'v13', 'v14', 'v15'],
  },
  visualizationSettings: {
    byId: {
      vs0: {
        id: 'vs0',
        columnStates: [
          {
            colId: 'runningPosition',
            width: 68,
            hide: false,
            pinned: null,
            sort: 'asc',
            sortIndex: 0,
            aggFunc: null,
            rowGroup: false,
            rowGroupIndex: null,
            pivot: false,
            pivotIndex: null,
            flex: 1,
          },
          {
            colId: 'vehicleNumber',
            width: 68,
            hide: false,
            pinned: null,
            sort: null,
            sortIndex: null,
            aggFunc: null,
            rowGroup: false,
            rowGroupIndex: null,
            pivot: false,
            pivotIndex: null,
            flex: 1,
          },
          {
            colId: 'driverFullName',
            width: 200,
            hide: true,
            pinned: null,
            sort: null,
            sortIndex: null,
            aggFunc: null,
            rowGroup: false,
            rowGroupIndex: null,
            pivot: false,
            pivotIndex: null,
            flex: 1,
          },
          {
            colId: 'lapsCompleted',
            width: 68,
            hide: false,
            pinned: null,
            sort: null,
            sortIndex: null,
            aggFunc: null,
            rowGroup: false,
            rowGroupIndex: null,
            pivot: false,
            pivotIndex: null,
            flex: 1,
          },
          {
            colId: 'deltaToTarget',
            width: 100,
            hide: false,
            pinned: null,
            sort: null,
            sortIndex: null,
            aggFunc: null,
            rowGroup: false,
            rowGroupIndex: null,
            pivot: false,
            pivotIndex: null,
            flex: 1,
          },
          {
            colId: 'lastLapTime',
            width: 70,
            hide: false,
            pinned: null,
            sort: null,
            sortIndex: null,
            aggFunc: null,
            rowGroup: false,
            rowGroupIndex: null,
            pivot: false,
            pivotIndex: null,
            flex: 1,
          },
          {
            colId: 'bestLapTime',
            width: 70,
            hide: false,
            pinned: null,
            sort: null,
            sortIndex: null,
            aggFunc: null,
            rowGroup: false,
            rowGroupIndex: null,
            pivot: false,
            pivotIndex: null,
            flex: 1,
          },
          {
            colId: 'lapsLed',
            width: 200,
            hide: true,
            pinned: null,
            sort: null,
            sortIndex: null,
            aggFunc: null,
            rowGroup: false,
            rowGroupIndex: null,
            pivot: false,
            pivotIndex: null,
            flex: 1,
          },
          {
            colId: 'movingAverageLapTime',
            width: 200,
            hide: true,
            pinned: null,
            sort: null,
            sortIndex: null,
            aggFunc: null,
            rowGroup: false,
            rowGroupIndex: null,
            pivot: false,
            pivotIndex: null,
            flex: 1,
          },
          {
            colId: 'runName',
            width: 68,
            hide: false,
            pinned: null,
            sort: null,
            sortIndex: null,
            aggFunc: null,
            rowGroup: false,
            rowGroupIndex: null,
            pivot: false,
            pivotIndex: null,
            flex: 1,
          },
          {
            colId: 'ppLap',
            width: 200,
            hide: true,
            pinned: null,
            sort: null,
            sortIndex: null,
            aggFunc: null,
            rowGroup: false,
            rowGroupIndex: null,
            pivot: false,
            pivotIndex: null,
            flex: 1,
          },
          {
            colId: 'ppStrat',
            width: 200,
            hide: true,
            pinned: null,
            sort: null,
            sortIndex: null,
            aggFunc: null,
            rowGroup: false,
            rowGroupIndex: null,
            pivot: false,
            pivotIndex: null,
            flex: 1,
          },
        ],
        localVehicleSelections: {
          useLocalSelections: false,
          selectedGroup: 'none',
          selectedOems: [],
          selectedPerformances: [],
          selectedPlayoffDrivers: 'none',
          selectedPlayoffTopDrivers: 'none',
          selectedLeader: 'leaderOnly',
          individualSelections: [],
        },
        dataFeedSelections: OriginType.MULTILOOP,
      },
      vs2: {
        id: 'vs2',
        localVehicleSelections: {
          useLocalSelections: false,
          selectedGroup: 'none',
          selectedOems: [],
          selectedPerformances: [],
          selectedPlayoffTopDrivers: 'none',
          selectedPlayoffDrivers: 'none',
          selectedLeader: 'leaderOnly',
          individualSelections: [],
        },
        focusedTab: 2,
      },
      vs3: {
        id: 'vs3',
        localVehicleSelections: {
          useLocalSelections: false,
          selectedGroup: 'none',
          selectedOems: [],
          selectedPerformances: [],
          selectedPlayoffTopDrivers: 'none',
          selectedPlayoffDrivers: 'none',
          selectedLeader: 'leaderOnly',
          individualSelections: [],
        },
        radiusRings: false,
      },
      vs9: {
        id: 'vs9',
        columnStates: [
          {
            colId: 'cameraTakeTimestamp',
            flex: 1,
            sort: 'desc',
          },
          {
            colId: 'cameraLocation',
            flex: 1,
          },
          {
            colId: 'lowLineTemperature',
            flex: 1,
          },
          {
            colId: 'middleLineTemperature',
            flex: 1,
          },
          {
            colId: 'highLineTemperature',
            flex: 1,
          },
          {
            colId: 'referenceTemperature',
            flex: 1,
          },
        ],
        localVehicleSelections: {
          useLocalSelections: false,
          selectedGroup: 'none',
          selectedOems: [],
          selectedPerformances: [],
          selectedPlayoffTopDrivers: 'none',
          selectedPlayoffDrivers: 'none',
          selectedLeader: 'leaderOnly',
          individualSelections: [],
        },
        showHeatMap: true,
        showGraph: true,
        showTable: true,
        isLive: false,
      },
      vs4: {
        id: 'vs4',
        localVehicleSelections: {
          useLocalSelections: false,
          selectedGroup: 'none',
          selectedOems: [],
          selectedPerformances: [],
          selectedPlayoffTopDrivers: 'none',
          selectedPlayoffDrivers: 'none',
          selectedLeader: 'leaderOnly',
          individualSelections: [],
        },
        columnStates: [
          {
            colId: 'fastestPosition',
            hide: true,
          },
          {
            colId: 'runningPosition',
            hide: true,
          },
        ],
        dataFeedSelections: OriginType.MULTILOOP,
        localVehicleSelectionsSortOrder: [],
        sortOrder: ['1', '3', '5', '8', '9', '24', '48', '99'],
      },
      vs5: {
        id: 'vs5',
      },
      vs6: {
        id: 'vs6',
        lapsFrom: 1,
        lapsAutoIncrement: false,
        timeofDayEnabled: false,
        localVehicleSelections: {
          useLocalSelections: false,
          selectedGroup: 'none',
          selectedOems: [],
          selectedPerformances: [],
          selectedPlayoffTopDrivers: 'none',
          selectedPlayoffDrivers: 'none',
          selectedLeader: 'leaderOnly',
          individualSelections: [],
        },
        dataFeedSelections: OriginType.MULTILOOP,
        isInlineSettingsShown: true,
        isVehicleLegendShown: true,
        lastEventIdUsed: 5303,
        timeInterval: 0.5,
        timeThresholdMaximum: 31.4,
        timeThresholdMinimum: 29,
      },
      vs7: {
        id: 'vs7',
        focusedTab: 1,
      },
      vs8: {
        id: 'vs8',
        columnStates: [
          {
            colId: 'runName',
            width: 82,
            hide: false,
            pinned: 'left',
            sort: null,
            sortIndex: null,
            aggFunc: null,
            rowGroup: false,
            rowGroupIndex: null,
            pivot: false,
            pivotIndex: null,
            flex: 1,
          },
          {
            colId: 'runningPosition',
            width: 66,
            hide: false,
            pinned: null,
            sort: 'asc',
            sortIndex: null,
            aggFunc: null,
            rowGroup: false,
            rowGroupIndex: null,
            pivot: false,
            pivotIndex: null,
            flex: 1,
          },
          {
            colId: 'driverFullName',
            width: 200,
            hide: true,
            pinned: null,
            sort: null,
            sortIndex: null,
            aggFunc: null,
            rowGroup: false,
            rowGroupIndex: null,
            pivot: false,
            pivotIndex: null,
            flex: 1,
          },
          {
            colId: 'ppLap',
            width: 200,
            hide: true,
            pinned: null,
            sort: null,
            sortIndex: null,
            aggFunc: null,
            rowGroup: false,
            rowGroupIndex: null,
            pivot: false,
            pivotIndex: null,
            flex: 1,
          },
          {
            colId: 'ppStrat',
            width: 200,
            hide: true,
            pinned: null,
            sort: null,
            sortIndex: null,
            aggFunc: null,
            rowGroup: false,
            rowGroupIndex: null,
            pivot: false,
            pivotIndex: null,
            flex: 1,
          },
          {
            colId: 'vehicleNumber',
            width: 80,
            hide: false,
            pinned: null,
            sort: null,
            sortIndex: null,
            aggFunc: null,
            rowGroup: false,
            rowGroupIndex: null,
            pivot: false,
            pivotIndex: null,
            flex: 1,
          },
          {
            colId: 'lapsCompleted',
            width: 69,
            hide: false,
            pinned: null,
            sort: null,
            sortIndex: null,
            aggFunc: null,
            rowGroup: false,
            rowGroupIndex: null,
            pivot: false,
            pivotIndex: null,
            flex: 1,
          },
          {
            colId: 'deltaToTarget',
            width: 100,
            hide: false,
            pinned: null,
            sort: null,
            sortIndex: null,
            aggFunc: null,
            rowGroup: false,
            rowGroupIndex: null,
            pivot: false,
            pivotIndex: null,
            flex: 1,
          },
          {
            colId: 'lastLapTime',
            width: 104,
            hide: false,
            pinned: null,
            sort: null,
            sortIndex: null,
            aggFunc: null,
            rowGroup: false,
            rowGroupIndex: null,
            pivot: false,
            pivotIndex: null,
            flex: 1,
          },
          {
            colId: 'bestLapTime',
            width: 105,
            hide: false,
            pinned: null,
            sort: null,
            sortIndex: null,
            aggFunc: null,
            rowGroup: false,
            rowGroupIndex: null,
            pivot: false,
            pivotIndex: null,
            flex: 1,
          },
          {
            colId: 'lapsLed',
            width: 200,
            hide: true,
            pinned: null,
            sort: null,
            sortIndex: null,
            aggFunc: null,
            rowGroup: false,
            rowGroupIndex: null,
            pivot: false,
            pivotIndex: null,
            flex: null,
          },
          {
            colId: 'movingAverageLapTime',
            width: 200,
            hide: true,
            pinned: null,
            sort: null,
            sortIndex: null,
            aggFunc: null,
            rowGroup: false,
            rowGroupIndex: null,
            pivot: false,
            pivotIndex: null,
            flex: 1,
          },
        ],
        localVehicleSelections: {
          useLocalSelections: false,
          selectedGroup: 'oem',
          selectedOems: [],
          selectedPerformances: [],
          selectedPlayoffDrivers: 'none',
          selectedPlayoffTopDrivers: 'none',
          selectedLeader: 'leaderOnly',
          individualSelections: [],
        },
        dataFeedSelections: OriginType.MULTILOOP,
      },
      vs10: {
        id: 'vs10',
        lapsFrom: 1,
        lapsAutoIncrement: false,
        timeofDayEnabled: false,
        localVehicleSelections: {
          useLocalSelections: false,
          selectedGroup: 'none',
          selectedOems: [],
          selectedPerformances: [],
          selectedPlayoffTopDrivers: 'none',
          selectedPlayoffDrivers: 'none',
          selectedLeader: 'leaderOnly',
          individualSelections: [],
        },
        dataFeedSelections: OriginType.MULTILOOP,
        isInlineSettingsShown: true,
        isVehicleLegendShown: true,
        lastEventIdUsed: 5303,
        timeInterval: 0.5,
        timeThresholdMaximum: 31.4,
        timeThresholdMinimum: 29,
      },
      vs11: {
        id: 'vs11',
        localVehicleSelections: {
          useLocalSelections: false,
          selectedGroup: 'none',
          selectedOems: [],
          selectedPerformances: [],
          selectedPlayoffTopDrivers: 'none',
          selectedPlayoffDrivers: 'none',
          selectedLeader: 'leaderOnly',
          individualSelections: [],
        },
        columnStates: [],
        dataFeedSelections: OriginType.MULTILOOP,
        localVehicleSelectionsSortOrder: [],
        sortOrder: ['1', '3', '5', '8', '9', '24', '48', '99'],
      },
      vs12: {
        id: 'vs12',
      },
      vs13: {
        id: 'vs13',
        showLeaderIndicator: true,
        showLuckyDogIndicator: false,
        showOffLeadLapVehiclesIndicator: false,
        localVehicleSelections: {
          useLocalSelections: false,
          selectedGroup: 'none',
          selectedOems: [],
          selectedPerformances: [],
          selectedPlayoffTopDrivers: 'none',
          selectedPlayoffDrivers: 'none',
          selectedLeader: 'leaderOnly',
          individualSelections: [],
        },
      },
      vs14: {
        id: 'vs14',
        localVehicleSelections: {
          useLocalSelections: false,
          selectedGroup: 'none',
          selectedOems: [],
          selectedPerformances: [],
          selectedPlayoffTopDrivers: 'none',
          selectedPlayoffDrivers: 'none',
          selectedLeader: 'leaderOnly',
          individualSelections: [],
        },
        dataFeedSelections: OriginType.MULTILOOP,
        slowTime: undefined,
        mediumTime: undefined,
        fastTime: undefined,
        sortType: 'vehicleNumber',
        slowThresholdColor: 'highlight-yellow-200',
        slowThresholdColorPallete: 'highlight',
        slowColorPallete: 'highlight',
        slowColor: 'highlight-yellow-400',
        mediumColor: 'highlight-green-400',
        mediumColorPallete: 'highlight',
        fastColor: 'highlight-green-600',
        fastColorPallete: 'highlight',
        isSlowThresholdColorEnabled: true,
        isSlowColorEnabled: false,
        isMediumColorEnabled: true,
        isFastColorEnabled: true,
        xLap: -1,
        lastEventIdUsed: 5303,
      },
      vs15: {
        id: 'vs15',
        columnStates: [
          {
            colId: 'fastLapRank',
          },
          {
            colId: 'vehicleNumber',
          },
          {
            colId: 'vehicleManufacturer',
          },
          {
            colId: 'fastLap',
          },
          {
            colId: 'fastLapDiff',
          },
          {
            colId: 'fast10VehicleNumber',
          },
          {
            colId: 'fast10VehicleManufacturer',
          },
          {
            colId: 'fast10',
          },
          {
            colId: 'fast10Diff',
          },
          {
            colId: 'fast20VehicleNumber',
          },
          {
            colId: 'fast20VehicleManufacturer',
          },
          {
            colId: 'fast20',
          },
          {
            colId: 'fast20Diff',
          },
          {
            colId: 'fast30VehicleNumber',
          },
          {
            colId: 'fast30VehicleManufacturer',
          },
          {
            colId: 'fast30',
          },
          {
            colId: 'fast30Diff',
          },
          {
            colId: 'fastXVehicleNumber',
          },
          {
            colId: 'fastXVehicleManufacturer',
          },
          {
            colId: 'fastX',
          },
          {
            colId: 'fastXDiff',
          },
        ],
        localVehicleSelections: {
          useLocalSelections: false,
          selectedGroup: 'none',
          selectedOems: [],
          selectedPerformances: [],
          selectedPlayoffTopDrivers: 'none',
          selectedPlayoffDrivers: 'none',
          selectedLeader: 'leaderOnly',
          individualSelections: [],
        },
        topXLaps: 50,
        lastEventIdUsed: 5303,
      },
    },
    allIds: [
      'vs0',
      'vs2',
      'vs3',
      'vs9',
      'vs4',
      'vs5',
      'vs6',
      'vs7',
      'vs8',
      'vs10',
      'vs11',
      'vs12',
      'vs13',
      'vs14',
      'vs15',
    ],
  },
  selectedCompetitorGroups: {
    1: 'vc1',
  },
  competitorGroups: {
    byId: {
      vc1: {
        id: 'vc1',
        name: '',
        seriesId: '1',
        vehicles: [
          'vh1',
          'vh2',
          'vh3',
          'vh4',
          'vh5',
          'vh6',
          'vh7',
          'vh8',
          'vh9',
          'vh10',
          'vh11',
          'vh12',
          'vh13',
          'vh14',
          'vh15',
          'vh16',
          'vh17',
          'vh18',
          'vh19',
          'vh20',
          'vh21',
          'vh22',
          'vh23',
          'vh24',
          'vh25',
          'vh26',
          'vh27',
          'vh28',
          'vh29',
          'vh30',
          'vh31',
          'vh32',
          'vh33',
          'vh34',
          'vh35',
          'vh36',
        ],
      },
    },
    allIds: ['vc1'],
  },
  competitorGroupVehicles: {
    byId: {
      vh1: {
        id: 'vh1',
        vehicleNumber: '78',
        colorId: '37',
        highlighted: false,
      },
      vh2: {
        id: 'vh2',
        vehicleNumber: '77',
        colorId: '35',
        highlighted: false,
      },
      vh3: {
        id: 'vh3',
        vehicleNumber: '1',
        colorId: '30',
        highlighted: true,
      },
      vh4: {
        id: 'vh4',
        vehicleNumber: '3',
        colorId: '1',
        highlighted: true,
      },
      vh5: {
        id: 'vh5',
        vehicleNumber: '5',
        colorId: 'fuchsia',
        highlighted: true,
      },
      vh6: {
        id: 'vh6',
        vehicleNumber: '8',
        colorId: '24',
        highlighted: true,
      },
      vh7: {
        id: 'vh7',
        vehicleNumber: '9',
        colorId: 'amaranthRed',
        highlighted: true,
      },
      vh8: {
        id: 'vh8',
        vehicleNumber: '24',
        colorId: 'burningOrange',
        highlighted: true,
      },
      vh9: {
        id: 'vh9',
        vehicleNumber: '99',
        colorId: 'royalBlue',
        highlighted: true,
      },
      vh10: {
        id: 'vh10',
        vehicleNumber: '48',
        colorId: '12',
        highlighted: true,
      },
      vh11: {
        id: 'vh11',
        vehicleNumber: '2',
        colorId: '37',
        highlighted: false,
      },
      vh12: {
        id: 'vh12',
        vehicleNumber: '4',
        colorId: '37',
        highlighted: false,
      },
      vh13: {
        id: 'vh13',
        vehicleNumber: '10',
        colorId: '37',
        highlighted: false,
      },
      vh14: {
        id: 'vh14',
        vehicleNumber: '7',
        colorId: '35',
        highlighted: false,
      },
      vh15: {
        id: 'vh15',
        vehicleNumber: '6',
        colorId: '37',
        highlighted: false,
      },
      vh16: {
        id: 'vh16',
        vehicleNumber: '11',
        colorId: '33',
        highlighted: false,
      },
      vh17: {
        id: 'vh17',
        vehicleNumber: '12',
        colorId: '37',
        highlighted: false,
      },
      vh18: {
        id: 'vh18',
        vehicleNumber: '17',
        colorId: '37',
        highlighted: false,
      },
      vh19: {
        id: 'vh19',
        vehicleNumber: '14',
        colorId: '37',
        highlighted: false,
      },
      vh20: {
        id: 'vh20',
        vehicleNumber: '20',
        colorId: '33',
        highlighted: false,
      },
      vh21: {
        id: 'vh21',
        vehicleNumber: '15',
        colorId: '37',
        highlighted: false,
      },
      vh22: {
        id: 'vh22',
        vehicleNumber: '16',
        colorId: '35',
        highlighted: false,
      },
      vh23: {
        id: 'vh23',
        vehicleNumber: '22',
        colorId: '37',
        highlighted: false,
      },
      vh24: {
        id: 'vh24',
        vehicleNumber: '21',
        colorId: '37',
        highlighted: false,
      },
      vh25: {
        id: 'vh25',
        vehicleNumber: '19',
        colorId: '33',
        highlighted: false,
      },
      vh26: {
        id: 'vh26',
        vehicleNumber: '47',
        colorId: '35',
        highlighted: false,
      },
      vh27: {
        id: 'vh27',
        vehicleNumber: '38',
        colorId: '37',
        highlighted: false,
      },
      vh28: {
        id: 'vh28',
        vehicleNumber: '34',
        colorId: '37',
        highlighted: false,
      },
      vh29: {
        id: 'vh29',
        vehicleNumber: '45',
        colorId: '33',
        highlighted: false,
      },
      vh30: {
        id: 'vh30',
        vehicleNumber: '43',
        colorId: '35',
        highlighted: false,
      },
      vh31: {
        id: 'vh31',
        vehicleNumber: '31',
        colorId: '35',
        highlighted: false,
      },
      vh32: {
        id: 'vh32',
        vehicleNumber: '42',
        colorId: '35',
        highlighted: false,
      },
      vh33: {
        id: 'vh33',
        vehicleNumber: '41',
        colorId: '37',
        highlighted: false,
      },
      vh34: {
        id: 'vh34',
        vehicleNumber: '54',
        colorId: '33',
        highlighted: false,
      },
      vh35: {
        id: 'vh35',
        vehicleNumber: '51',
        colorId: '37',
        highlighted: false,
      },
      vh36: {
        id: 'vh36',
        vehicleNumber: '23',
        colorId: '33',
        highlighted: false,
      },
    },
    allIds: [
      'vh1',
      'vh2',
      'vh3',
      'vh4',
      'vh5',
      'vh6',
      'vh7',
      'vh8',
      'vh9',
      'vh10',
      'vh11',
      'vh12',
      'vh13',
      'vh14',
      'vh15',
      'vh16',
      'vh17',
      'vh18',
      'vh19',
      'vh20',
      'vh21',
      'vh22',
      'vh23',
      'vh24',
      'vh25',
      'vh26',
      'vh27',
      'vh28',
      'vh29',
      'vh30',
      'vh31',
      'vh32',
      'vh33',
      'vh34',
      'vh35',
      'vh36',
    ],
  },
  selectedReferenceVehicle: createEntity(),
  hideFooter: false,
  trackMapSettings: createEntity(),
};
